/* eslint-disable @stylistic/ts/brace-style */
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    inject,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
    Validators,
} from '@angular/forms';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    isDefined,
    SignaturePad,
    SignaturePadOptions,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, Observable, skip, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    pairwise,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateSystemConditionSignature,
    updateSystemConditionSignatureDisabled,
    updateSystemConditionSignatureName,
} from '../../store/actions/functional-elements.actions';
import { getFormParticipantCopyReadonly } from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { FormsConstants } from '../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../types/functional-element';
import { Signature } from '../../types/signature';

@Component({
    selector: 'pace-forms-system-condition-signature',
    templateUrl: './system-condition-signature.component.html',
    styleUrls: ['./system-condition-signature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SystemConditionSignatureComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SystemConditionSignatureComponent),
            multi: true,
        },
    ],
})
export class SystemConditionSignatureComponent
    implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, Validator
{
    private readonly configService = inject(ConfigService);
    @Input()
    public signature$: Observable<Signature>;

    @ViewChild('signatureCanvas')
    public signatureCanvas: ElementRef;

    public signaturePad: SignaturePad | undefined;

    public readOnly$: Observable<boolean>;
    public displayNACheckboxForCustomerSignature$: Observable<boolean>;
    public displayHistoryButton$: Observable<boolean>;

    private clearSignature$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    private signatureInitializedSubject = new Subject<void>();

    public signatureForm: FormGroup;
    public firstNameControl: FormControl;
    public lastNameControl: FormControl;
    public checkboxControl: FormControl;

    public FUNCTIONAL_ELEMENT_TYPE = FUNCTIONAL_ELEMENT_TYPE;
    public faCheck = faCheck;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly store: Store<FormsState>,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
    }

    public ngOnInit(): void {
        // esli nt-disable-next-line @typescript-eslint/unbound-method
        this.firstNameControl = this.formBuilder.control('');
        // esli nt-disable-next-line @typescript-eslint/unbound-method
        this.lastNameControl = this.formBuilder.control('');
        this.checkboxControl = this.formBuilder.control('');
        this.signatureForm = this.formBuilder.group({
            firstName: this.firstNameControl,
            lastName: this.lastNameControl,
            checkbox: this.checkboxControl,
        });
        this.onChange();

        this.signatureInitializedSubject
            .asObservable()
            .pipe(
                exhaustMap(() =>
                    combineLatest([this.signature$, this.readOnly$]).pipe(
                        tap(([signature, readOnly]) => {
                            // enab le or disable controls based on state
                            this.firstNameControl[signature.disabled || readOnly ? 'disable' : 'enable']({
                                emitEvent: false,
                                onlySelf: true,
                            });
                            this.lastNameControl[signature.disabled || readOnly ? 'disable' : 'enable']({
                                emitEvent: false,
                                onlySelf: true,
                            });
                            // Chec kbox gets only disabled when the entire form is set to "readonly", otherwise the user has to way
                            // to e nable the controls again
                            this.checkboxControl[readOnly ? 'disable' : 'enable']({
                                emitEvent: false,
                                onlySelf: true,
                            });

                            // rest ore existing values from state
                            this.firstNameControl.patchValue(signature.firstName, {
                                emitEvent: false,
                                onlySelf: true,
                            });
                            this.lastNameControl.patchValue(signature.lastName, {
                                emitEvent: false,
                                onlySelf: true,
                            });
                            this.checkboxControl.patchValue(signature.disabled, {
                                emitEvent: false,
                                onlySelf: true,
                            });

                            // fix  validators
                            if (signature.disabled) {
                                this.firstNameControl.clearValidators();
                                this.lastNameControl.clearValidators();
                            } else {
                                // eslint-disable-next-line @typescript-eslint/unbound-method
                                this.firstNameControl.setValidators(Validators.required);
                                // eslint-disable-next-line @typescript-eslint/unbound-method
                                this.lastNameControl.setValidators(Validators.required);
                            }

                            // Need to trigger updateValueAndValidity after removing/setting validators.
                            // See  link for more details https://stackoverflow.com/questions/38797414/in-angular-how-to-add-validator-to-formcontrol-after-control-is-created
                            this.firstNameControl.updateValueAndValidity();
                            this.lastNameControl.updateValueAndValidity();

                            // fix  signature pad state
                            if (
                                // rest ore signature when we restore a saved state: Then the signature pad is still empty, but
                                // the  stored signature state contains data
                                this.signaturePad?.isEmpty &&
                                signature.signatureDataPoints.length > 0
                            ) {
                                this.signaturePad?.off();
                                this.signaturePad?.restoreFromDataPoints(signature.signatureDataPoints);
                            } else if (
                                !this.signaturePad?.isEmpty &&
                                signature.signature.length === 0 &&
                                signature.signatureDataPoints.length === 0
                            ) {
                                this.clearSignature$.next();
                            }
                            if (signature.disabled || readOnly) {
                                this.signaturePad?.off();
                            } else {
                                this.signaturePad?.on();
                            }

                            // call form validation callback
                            this.onChange();
                            this.changeDetectorRef.detectChanges();
                        }),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.firstNameControl.valueChanges
            .pipe(
                // distinctUntilChanged is not working properly together with valueChanges:
                // in-depth explanation: https://stackoverflow.com/questions/46889851/formcontrol-detectchanges-why-use-distinctuntilchanged
                skip(1),
                distinctUntilChanged(),
                tap(() => {
                    this.changeDetectorRef.detectChanges();
                }),
                debounceTime(ConfigConstants.debounceTimeMs),
                withLatestFrom(this.signature$),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, signature]: [string | null, Signature]) => {
                this.store.dispatch(
                    updateSystemConditionSignatureName({
                        signatureType: signature.type,
                        xmlId: signature.nameXmlId,
                        firstName: newValue ?? '',
                        lastName: signature.lastName,
                    }),
                );
            });

        this.lastNameControl.valueChanges
            .pipe(
                // distinctUntilChanged is not working properly together with valueChanges:
                // in-depth explanation: https://stackoverflow.com/questions/46889851/formcontrol-detectchanges-why-use-distinctuntilchanged
                skip(1),
                distinctUntilChanged(),
                tap(() => {
                    this.changeDetectorRef.detectChanges();
                }),
                debounceTime(ConfigConstants.debounceTimeMs),
                withLatestFrom(this.signature$),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, signature]: [string | null, Signature]) => {
                this.store.dispatch(
                    updateSystemConditionSignatureName({
                        signatureType: signature.type,
                        xmlId: signature.nameXmlId,
                        firstName: signature.firstName,
                        lastName: newValue ?? '',
                    }),
                );
            });

        this.checkboxControl.valueChanges
            .pipe(withLatestFrom(this.signature$), takeUntil(this.destroy$))
            .subscribe(([checked, signature]: [boolean | null, Signature]) => {
                this.store.dispatch(
                    updateSystemConditionSignatureName({
                        signatureType: signature.type,
                        xmlId: signature.nameXmlId,
                        firstName: checked ? FormsConstants.NA : '',
                        lastName: checked ? FormsConstants.NA : '',
                    }),
                );

                this.store.dispatch(
                    updateSystemConditionSignature({
                        signatureType: signature.type,
                        signatureDataPoints: [],
                        newSignature: '',
                        dateXmlId: signature.dateXmlId,
                        date: isDefined(signature.choices)
                            ? moment().format(ConfigConstants.formatPatternDateCosima)
                            : moment().format(this.clientManager.getDateFormat(false)),
                    }),
                );
                this.store.dispatch(
                    updateSystemConditionSignatureDisabled({
                        signatureType: signature.type,
                        disabled: checked ?? false,
                    }),
                );

                if (checked) {
                    this.signaturePad?.off();
                    this.firstNameControl.clearValidators();
                    this.lastNameControl.clearValidators();
                    this.firstNameControl.disable({ emitEvent: false });
                    this.lastNameControl.disable({ emitEvent: false });
                } else {
                    this.signaturePad?.on();
                    // eslint-disable-next-line @typescript-eslint/unbound-method
                    this.firstNameControl.setValidators(Validators.required);
                    // eslint-disable-next-line @typescript-eslint/unbound-method
                    this.lastNameControl.setValidators(Validators.required);
                    this.firstNameControl.enable({ emitEvent: false });
                    this.lastNameControl.enable({ emitEvent: false });
                }
                this.changeDetectorRef.detectChanges();
            });

        this.clearSignature$
            .asObservable()
            .pipe(withLatestFrom(this.signature$), takeUntil(this.destroy$))
            .subscribe(([, signature]) => {
                this.signaturePad?.clear();
                this.store.dispatch(
                    updateSystemConditionSignature({
                        signatureType: signature.type,
                        signatureDataPoints: [],
                        newSignature: '',
                        dateXmlId: signature.dateXmlId,
                        date: isDefined(signature.choices)
                            ? moment().format(ConfigConstants.formatPatternDateCosima)
                            : moment().format(this.clientManager.getDateFormat(false)),
                    }),
                );
            });

        this.displayNACheckboxForCustomerSignature$ = this.signature$.pipe(
            map(
                (signature) =>
                    signature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION ||
                    signature.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
            ),
        );

        this.displayHistoryButton$ = this.signature$.pipe(
            map(
                (signature) =>
                    isDefined(signature.uniqueId) &&
                    signature.uniqueId.length > 0 &&
                    this.configService.getEnableFormsHistoricalValues(),
            ),
        );
    }

    public onChange(): void {
        // Plac eholder - do nothing
    }
    public onTouched(): void {
        // Plac eholder - do nothing
    }

    public ngAfterViewInit(): void {
        const signatureOptions: SignaturePadOptions = {
            outputImageType: 'image/jpeg',
            outputImageMaxSize: 9000,
            backgroundColor: 'rgba(255, 255, 255, 1)',
        };

        this.signaturePad = new SignaturePad(
            this.log,
            <HTMLCanvasElement>this.signatureCanvas.nativeElement,
            signatureOptions,
        );

        if (typeof this.signaturePad === 'undefined') {
            throw new Error('SystemConditionSignatureComponent: Signature pad not found!');
        }

        // we just subscribing to the datapoints because we are need them for restoring the signature
        // Furthermore pairwise is necessary to get the previous value from datapoints to compare them against the new value of datapoints
        // we expecting a new length for the incoming datapoint array
        this.signaturePad.dataPoints$
            .pipe(
                pairwise(),
                filter(([oldDatapoints, dataPoints]) => oldDatapoints.length !== dataPoints.length),
                withLatestFrom(this.signaturePad.dataUrl$, this.signature$),
                takeUntil(this.destroy$),
            )
            .subscribe(([[, dataPoints], dataUrl, signature]) => {
                this.changeDetectorRef.detectChanges();
                this.store.dispatch(
                    updateSystemConditionSignature({
                        signatureType: signature.type,
                        signatureDataPoints: dataPoints,
                        newSignature: dataUrl,
                        dateXmlId: signature.dateXmlId,
                        date: isDefined(signature.choices)
                            ? moment().format(ConfigConstants.formatPatternDateCosima)
                            : moment().format(this.clientManager.getDateFormat(false)),
                    }),
                );
            });

        this.onChange();

        this.signatureInitializedSubject.next();
        // nece ssary to make the UI update its state based on the now existing signature pad
        this.changeDetectorRef.detectChanges();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public writeValue(): void {
        // do n othing.
    }

    public clearSignature(): void {
        this.clearSignature$.next();
    }

    public validate(): null | ValidationErrors {
        const errors: ValidationErrors = {};

        if (!this.checkboxControl.value) {
            if (
                typeof this.signaturePad === 'undefined' ||
                // sign ature pad may be empty if the control is deactivated; we can use the text input fields to detect this.
                (this.signaturePad.isEmpty && !this.firstNameControl.disabled)
            ) {
                errors['signature-missing'] = 'Signature is missing';
            }
            if (this.firstNameControl.invalid) {
                errors['first-name-missing'] = 'First name is missing';
            }
            if (this.lastNameControl.invalid) {
                errors['last-name-missing'] = 'Last name is missing';
            }
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
